import { cn } from '@exam-prep/ui';
import type { ReactNode, ComponentProps } from 'react';
import type { FieldRule } from '../lib/validation/field-rule';
import { useFieldValidity } from './use-field-validity';

/** Invalid controls get a red border once their message shows. */
const INVALID = 'aria-invalid:border-destructive aria-invalid:focus-visible:ring-destructive/40';

/** The inline message under a control that failed its rule. */
function FieldError({ id, message }: { id: string; message: string | null }) {
  if (!message) return null;
  return (
    <span id={id} role="alert" className="text-xs text-destructive">
      {message}
    </span>
  );
}

/** A validated text input. `rule` (from the request contract) adds its limits and messages. */
export function Input({
  className,
  rule,
  ...props
}: ComponentProps<'input'> & { rule?: FieldRule | undefined }) {
  const { ref, shown, errorId, attrs, controlProps } = useFieldValidity<HTMLInputElement>(
    rule,
    props.value,
    props,
  );
  return (
    <>
      <input
        className={cn(
          'h-9 w-full rounded-md border border-input bg-background px-3 text-sm transition-colors placeholder:text-muted-foreground hover:border-brand-amber focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring/40 focus-visible:outline-none',
          INVALID,
          className,
        )}
        {...attrs}
        {...props}
        {...controlProps}
        ref={ref}
      />
      <FieldError id={errorId} message={shown} />
    </>
  );
}

/** A validated textarea, like {@link Input}. */
export function Textarea({
  className,
  rule,
  ...props
}: ComponentProps<'textarea'> & { rule?: FieldRule | undefined }) {
  const { ref, shown, errorId, attrs, controlProps } = useFieldValidity<HTMLTextAreaElement>(
    rule,
    props.value,
    props,
  );
  return (
    <>
      <textarea
        className={cn(
          'w-full rounded-md border border-input bg-background p-3 font-mono text-xs transition-colors hover:border-brand-amber focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring/40 focus-visible:outline-none',
          INVALID,
          className,
        )}
        {...attrs}
        {...props}
        {...controlProps}
        ref={ref}
      />
      <FieldError id={errorId} message={shown} />
    </>
  );
}

export function Select({ className, ...props }: ComponentProps<'select'>) {
  return (
    <select
      className={cn('h-9 rounded-md border border-input bg-background px-2 text-sm', className)}
      {...props}
    />
  );
}

/**
 * A labelled control with an optional hint. While the control shows its error, the hint hides,
 * so the field carries one message at a time (the error says what to fix, the hint says how).
 */
export function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <label className="group/field flex flex-col gap-1 text-sm">
      <span className="font-medium">{label}</span>
      {children}
      {hint ? (
        <span className="text-xs text-muted-foreground group-has-[[aria-invalid=true]]/field:hidden">
          {hint}
        </span>
      ) : null}
    </label>
  );
}
